import { logInfo, logError } from "common/debug-print";
import { getLocationSettings } from "./location";
import { browserMightBeOniOS, iOSVersion, shortUserAgent } from "./browser-detect";
import { standalone } from "webapp/lib/device";
import { ConditionVariable } from "common/condition-variable";
import { isDocumentHidden } from "./browser-hacks";
import {
    isServiceWorkerErrorMessage,
    makeCacheCleanupMessage,
    makeDbCleanupMessage,
} from "service-worker/shared-interface";
import { frontendSendEvent } from "common/tracing";
import { SyncJobQueue } from "common/sync-job-queue";
import { reloadBrowserWindow } from "./browser-reload";
import { withTimeout } from "common/support/timeout";
import { sleep } from "common/Support";

let serviceWorkerInstalled = false;
const serviceWorkerInstalledCV = new ConditionVariable();

export async function waitForInstalledServiceWorker(): Promise<void> {
    while (!serviceWorkerInstalled) {
        await serviceWorkerInstalledCV.wait();
    }
}

function setServiceWorkerInstalled() {
    serviceWorkerInstalled = true;
    serviceWorkerInstalledCV.notifyAll();
}

async function removeAllRegistrations(sw: ServiceWorkerContainer) {
    const registrations = await sw.getRegistrations();
    for (const registration of registrations) {
        await registration.unregister();
    }
}

export default function register() {
    if (!("serviceWorker" in navigator)) return setServiceWorkerInstalled();
    if (standalone && browserMightBeOniOS && iOSVersion[0] === 13) {
        // See https://github.com/djsweet/ios-pwa-freeze-bug:
        // The presence of Service Workers in iOS 13 PWAs causes them to freeze
        // when multitasking.
        logInfo("Removing service worker due to buggy iOS multitasking");
        window.addEventListener("load", () => {
            removeAllRegistrations(navigator.serviceWorker)
                .catch(() => {
                    /* Shut up ESlint, we don't care. */
                })
                .finally(() => setServiceWorkerInstalled());
        });
        return;
    }

    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.toString());
    if (publicUrl.origin !== window.location.origin) {
        // Our service worker won't work if PUBLIC_URL is on a different origin
        // from what our page is served on. This might happen if a CDN is used to
        // serve assets; see https://github.com/facebookincubator/create-react-app/issues/2374
        return setServiceWorkerInstalled();
    }

    window.addEventListener("load", () => {
        tryRegisterSW(getLocationSettings().serviceWorkerLocation);
    });
}

const cacheCleanupPeriod = 15_000;
const cacheGCTimeout = 10_000;
let controllerCleanupInterval: NodeJS.Timeout | undefined;
let controllerVisibilityCleanupHandler: (() => void) | undefined;

function possiblyDispatchWorkerError(ev: MessageEvent) {
    // We don't handle cross-origin messages here. If we did,
    // that'd be a potential security vulnerability.
    if (ev.origin !== window.location.origin) return;

    if (!isServiceWorkerErrorMessage(ev.data)) return;
    const { action, exceptionStr } = ev.data;

    // FIXME: We should send these as normal "exception" and "action"
    // fields. The only reason we don't is so we don't overwhelm the
    // alerts with possible spurious failures.
    frontendSendEvent("service worker exception", 0, {
        swException: exceptionStr,
        swAction: action,
    });
}

const serviceWorkerRegistrationTimeout = 5_000;
const serviceWorkerClearRegistrationTimeout = 2_000;
const controllersWithSetup = new WeakSet<ServiceWorker>();

function setupServiceWorkerListeners(controller: ServiceWorker | null) {
    if (!("serviceWorker" in navigator)) return;
    const { serviceWorker } = navigator;
    if (controller === null) return;
    if (controllersWithSetup.has(controller)) return;

    if (controllerCleanupInterval !== undefined) {
        clearInterval(controllerCleanupInterval);
    }
    if (controllerVisibilityCleanupHandler !== undefined) {
        document.removeEventListener("visibilitychange", controllerVisibilityCleanupHandler);
    }

    controllerVisibilityCleanupHandler = () => {
        if (isDocumentHidden()) return;
        controller.postMessage(makeDbCleanupMessage());
    };

    // Doing this both in an interval and a visibilitychange event listener
    // seems redundant, but there's a reason for it. Safari likes to forget
    // to dispatch events to background workers on page visibility transitions.
    // The hope behind this setup is that the cache cleanup message gets stuck
    // when being dispatched to the Service Worker, but further messages force
    // the Service Worker to be serviced immediately.
    controllerCleanupInterval = setInterval(controllerVisibilityCleanupHandler, cacheCleanupPeriod);
    document.addEventListener("visibilitychange", controllerVisibilityCleanupHandler);
    setTimeout(() => {
        controller.postMessage(makeCacheCleanupMessage());
    }, cacheGCTimeout);
    serviceWorker.addEventListener("message", possiblyDispatchWorkerError);

    // This little hack ensures that we have critical-path CSS and translation JS
    // available for offline use, even though we got them before we had a Service Worker.
    // Now that we have a Service Worker, fetching will automatically land them in the cache.
    const fetchQueue = new SyncJobQueue();
    const scripts = document.getElementsByTagName("script");
    for (let i = 0; i < scripts.length; i++) {
        const scriptElem = scripts[i];
        if (scriptElem === undefined) break;
        if (scriptElem.src.startsWith("/")) {
            void fetchQueue.run(() => fetch(scriptElem.src));
        }
    }

    const links = document.getElementsByTagName("link");
    for (let i = 0; i < links.length; i++) {
        const linkElem = links[i];
        if (linkElem === undefined) break;
        if (linkElem.href.startsWith("/")) {
            void fetchQueue.run(() => fetch(linkElem.href));
        }
    }

    controllersWithSetup.add(controller);
}

function tryRegisterSW(swUrl: string) {
    if (!("serviceWorker" in navigator)) {
        setServiceWorkerInstalled();
        return;
    }

    function setupServiceWorkerController(controller: ServiceWorker | null) {
        if (controller === null) {
            return setServiceWorkerInstalled();
        }
        controller.addEventListener("statechange", () => {
            if (controller.state === "activated") {
                setupServiceWorkerListeners(controller);
                return setServiceWorkerInstalled();
            }
        });
        controller.addEventListener("error", () => {
            if (controller.state === "installed" || controller.state === "activated") return;
            return setServiceWorkerInstalled();
        });

        if (controller.state === "activating") {
            setTimeout(async () => {
                // This keeps being a problem on Safari. What we know works is
                // forcibly reloading the page when the Service Worker gets stuck.
                //
                // We're trying extremely hard to _not_ get stuck but we can't seem
                // to catch everything.
                if (navigator.serviceWorker.controller?.state === "activating") {
                    logError("Service Worker got stuck activating");
                    await withTimeout(
                        removeAllRegistrations(navigator.serviceWorker),
                        undefined,
                        undefined,
                        serviceWorkerClearRegistrationTimeout
                    );
                    // We have to do this _after_ we unregister the Service Worker,
                    // or else the request to Honeycomb will get swallowed.
                    frontendSendEvent("stuck service worker", 0, {
                        standalone,
                        browser_might_be_on_ios: browserMightBeOniOS,
                        ios_major_version: iOSVersion[0],
                        ios_minor_version: iOSVersion[1],
                        userAgent: shortUserAgent,
                    });
                    // If we reload too quickly the outstanding request might get dropped.
                    await sleep(1_000);
                    reloadBrowserWindow("Service Worker got stuck activating");
                }
            }, serviceWorkerRegistrationTimeout);
        }
        if (controller.state === "activated") {
            setupServiceWorkerListeners(controller);
            return setServiceWorkerInstalled();
        }
    }

    if (navigator.serviceWorker.controller !== null) {
        setupServiceWorkerController(navigator.serviceWorker.controller);
    }
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        setupServiceWorkerController(navigator.serviceWorker.controller);
    });
    navigator.serviceWorker
        .register(swUrl)
        .then(registration => {
            if (registration.installing === null) {
                setupServiceWorkerController(registration.active);
            }
            registration.onupdatefound = () => {
                setupServiceWorkerController(registration.installing);
            };
        })
        .catch(error => {
            logError("Error during service worker registration:", error);
            setServiceWorkerInstalled();
        });
}
